import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  Checkbox,
  Skeleton,
  TableSortLabel,
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import { useSelector } from 'react-redux';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShieldIcon from '@mui/icons-material/Shield';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { RejectionReasonsModal, SelectOption } from '..';
import {
  formatedDate,
  threatsStatus,
  EXTERNAL_THREAT_PENDING_STATUS,
  isRejectionStatus,
} from '../../utils';
import {
  getDisplayThreatName,
  getThreatUrl,
  isPunycode,
  defangUrl,
} from '../../utils/domainUtils';
import { Span, ThreatDetails } from '../ThreatCard/ThreatCard.shared.style';
import { useChangeThreatStatusMutation } from '../../queries/threats';
import { useCompaniesQuery } from '../../queries/lookups';
import { useSuppressedThreatKeys } from '../../hooks/useSuppressedThreatKeys';
import { bulkOperationKeyForThreat } from '../../store/bulk/operations/slice';
import { ThreatTableProps, ThreatTypes } from '../../helpers/types';
import {
  ActionButtons,
  ThreatUrlCell,
  focusedRowSx,
} from '../Style/ThreatTable.style';
import { ExpandCell } from './ThreatExpandedRow.style';
import ThreatExpandedRow from './ThreatExpandedRow';
import SourceFaviconChip from './SourceFaviconChip';
import { BrpClassificationCell } from '../BrpClassification';
import ResurgentThreatIcon from '../ResurgentThreatIcon/ResurgentThreatIcon';
import {
  useTableKeyboardNavigation,
  CustomShortcut,
  ShortcutContext,
} from '../../hooks/useTableKeyboardNavigation';
import {
  BASE_SHORTCUTS,
  ESCAPE_WITH_DESELECT,
  SELECTION_SHORTCUTS,
  PAGINATION_SHORTCUTS,
  HELP_SHORTCUT,
  THREAT_TABLE_SHORTCUTS,
  REVIEW_MODE_SHORTCUTS,
} from '../../hooks/keyboardShortcuts.constants';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import ThreatPreviewPanel, { Kbd } from './ThreatPreviewPanel';

const REVIEW_PANEL_STORAGE_KEY = 'threats-review-panel-open';

// Statuses outside threatsStatus (e.g. possible-owned, external-threat-pending)
// must still render as the select's value — MUI shows blank without a matching
// MenuItem — but stay non-selectable (WHEEL-369).
const buildStatusOptions = (currentStatus?: string) => {
  const options: { label: string; value: string; disabled?: boolean }[] =
    threatsStatus.map(status => ({ label: status, value: status }));
  if (currentStatus && !threatsStatus.includes(currentStatus)) {
    options.unshift({
      label: currentStatus,
      value: currentStatus,
      disabled: true,
    });
  }
  return options;
};

const MIN_THREAT_NAME_LENGTH = 35;

const ThreatTable = ({
  threats,
  handleChangeSelectedThreats,
  isThreatSelected,
  threatNetworkData,
  userData,
  onSelectAll,
  onDeselectAll,
  currentPage,
  totalPages,
  onPageChange,
  isModalOpen,
  onBulkStatusChange,
  hasSelectedThreats,
}: ThreatTableProps) => {
  const [openRejectionModal, setOpenRejectionModal] = useState<boolean>(false);
  const [selectedThreat, setSelectedThreat] = useState<any>(null);
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [threatSort, setThreatSort] = useState<{
    columnKey: string;
    sortDirection: 'asc' | 'desc';
  } | null>(null);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [reviewPanelOpen, setReviewPanelOpen] = useState<boolean>(
    () => localStorage.getItem(REVIEW_PANEL_STORAGE_KEY) === 'true',
  );

  const toggleReviewPanel = useCallback(() => {
    setReviewPanelOpen(prev => {
      const next = !prev;
      localStorage.setItem(REVIEW_PANEL_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const changeStatusMutation = useChangeThreatStatusMutation();
  const { data: companiesData } = useCompaniesQuery();

  const pendingOperations = useSelector(
    (state: any) => state.bulkOperations.pendingOperations,
  );
  const suppressedThreatKeys = useSuppressedThreatKeys();

  const findCompanyNameById = useCallback(
    (id: number) => {
      const company = companiesData?.find(
        (company: { Id: number }) => company.Id === id,
      );
      return company ? company.Name : 'Unknown';
    },
    [companiesData],
  );

  const handleChangeThreatStatus = (threat: any, newStatus: string) => {
    if (
      isRejectionStatus(newStatus) &&
      threat.identification_status?.value === EXTERNAL_THREAT_PENDING_STATUS
    ) {
      // Rejecting (discard/benign) an external-threat-pending threat gates
      // on data entry — the rejection reasons modal stays.
      setSelectedThreat({ ...threat, newStatus });
      setOpenRejectionModal(true);
    } else {
      // Optimistic flow (WHEEL-316): apply immediately; the success toast
      // offers Undo instead of a confirmation modal.
      updateSingleStatus(threat, newStatus);
    }
  };
  const updateSingleStatus = async (threat: any, newStatus: string) => {
    const { protected_asset_id, threat_id, company_id, identification_status } =
      threat;
    // Where the row currently sits, so Undo can put it back (WHEEL-316).
    const restoreIndex = (threats?.items ?? []).findIndex(
      (item: any) =>
        item.threat_id === threat_id &&
        item.protected_asset_id === protected_asset_id &&
        item.company_id === company_id,
    );
    await changeStatusMutation
      .mutateAsync({
        status: newStatus,
        user_id: userData?.data?.Id,
        company_id,
        protected_asset_id,
        threat_id,
        previousStatus: identification_status?.value,
        threatItem: threat,
        restoreIndex: restoreIndex === -1 ? 0 : restoreIndex,
      })
      .catch(() => undefined); // error toast handled by the mutation
    setExpandedRowKey(null);
  };

  const updateSingleStatusWithRejection = async (
    rejectionReasonId: number,
    customRejectionReason?: string,
  ) => {
    const { protected_asset_id, threat_id, company_id, newStatus } =
      selectedThreat;
    await changeStatusMutation
      .mutateAsync({
        status: newStatus,
        user_id: userData?.data?.Id,
        company_id,
        protected_asset_id,
        threat_id,
        rejection_reason_id: rejectionReasonId,
        ...(customRejectionReason && {
          custom_rejection_reason: customRejectionReason,
        }),
      })
      .catch(() => undefined); // error toast handled by the mutation
    setOpenRejectionModal(false);
    setExpandedRowKey(null);
  };

  const handleSort = (columnKey: string) => {
    setThreatSort(prev => {
      if (!prev || prev.columnKey !== columnKey) {
        return { columnKey, sortDirection: 'asc' };
      }
      if (prev.sortDirection === 'asc') {
        return { columnKey, sortDirection: 'desc' };
      }
      return null;
    });
  };

  const getValue = useCallback(
    (item: any, columnKey: string) => {
      if (columnKey.startsWith('domainInfo')) {
        const threatId = item.threat_id;
        const nestedData = threatNetworkData[threatId];
        const domainInfo = nestedData?.whois ?? {};
        if (columnKey === 'domainInfo.registrar') {
          return domainInfo.registrar ?? '';
        }
        if (columnKey === 'domainInfo.registration_date') {
          return domainInfo.registration_date ?? '';
        }
        if (columnKey === 'domainInfo.expiration_date') {
          return domainInfo.expiration_date ?? '';
        }
      }
      return (
        columnKey.split('.').reduce((obj, prop) => obj?.[prop], item) ?? ''
      );
    },
    [threatNetworkData],
  );

  const compareValues = useCallback(
    (
      valueA: any,
      valueB: any,
      columnKey: string,
      sortDirection: 'asc' | 'desc',
      itemA?: any,
      itemB?: any,
    ): number => {
      if (columnKey === 'score') {
        const scoreA = typeof valueA?.value === 'number' ? valueA.value : null;
        const scoreB = typeof valueB?.value === 'number' ? valueB.value : null;

        if (scoreA === null && scoreB === null) return 0;
        if (scoreA === null) return sortDirection === 'asc' ? -1 : 1;
        if (scoreB === null) return sortDirection === 'asc' ? 1 : -1;
        return sortDirection === 'asc' ? scoreA - scoreB : scoreB - scoreA;
      }

      if (columnKey === 'company_name' && itemA && itemB) {
        const companyNameA = findCompanyNameById(itemA.company_id);
        const companyNameB = findCompanyNameById(itemB.company_id);
        return sortDirection === 'asc'
          ? companyNameA.localeCompare(companyNameB)
          : companyNameB.localeCompare(companyNameA);
      }

      const isValueAMissing = [null, '', '-'].includes(valueA);
      const isValueBMissing = [null, '', '-'].includes(valueB);

      if (isValueAMissing && !isValueBMissing)
        return sortDirection === 'asc' ? -1 : 1;
      if (!isValueAMissing && isValueBMissing)
        return sortDirection === 'asc' ? 1 : -1;
      if (isValueAMissing && isValueBMissing) return 0;

      if (columnKey.includes('date')) {
        const dateA = new Date(valueA);
        const dateB = new Date(valueB);
        return sortDirection === 'asc'
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      }

      const stringA = String(valueA).toLowerCase();
      const stringB = String(valueB).toLowerCase();
      return sortDirection === 'asc'
        ? stringA.localeCompare(stringB)
        : stringB.localeCompare(stringA);
    },
    [findCompanyNameById],
  );

  const sortItems = (
    items: any[],
    config: { columnKey: string; sortDirection: 'asc' | 'desc' } | null,
  ): any[] => {
    if (!config) return items;
    const { columnKey, sortDirection } = config;
    return [...items].sort((itemA, itemB) =>
      compareValues(
        getValue(itemA, columnKey),
        getValue(itemB, columnKey),
        columnKey,
        sortDirection,
        itemA,
        itemB,
      ),
    );
  };

  const sortedItems = useMemo(
    () => sortItems(threats?.items || [], threatSort),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threatSort, threats],
  );

  const copyThreatName = useCallback((name: string) => {
    try {
      navigator.clipboard.writeText(name);
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  }, []);

  const copyDefangedThreatName = (name: string) => {
    try {
      navigator.clipboard.writeText(defangUrl(name));
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  const handleExpandToggle = (rowKey: string) => {
    setExpandedRowKey(prev => (prev === rowKey ? null : rowKey));
  };

  // Extract displayItems into a useMemo so both the hook and the render use the same list
  const displayItems = useMemo(
    () => [
      // First show errored threats
      ...Object.values(pendingOperations)
        .filter((op: any) => op.hasError)
        .map((op: any) => op.originalData),
      // Then show regular threats
      ...sortedItems.filter((item: any) => {
        const key = bulkOperationKeyForThreat(item);
        // Errored rows are already rendered above, pinned to the top.
        if (pendingOperations?.[key]?.hasError) return false;
        // Rows still being processed by a bulk action stay hidden until their
        // threat_status_update event lands (or the TTL expires). The refetch
        // that follows a bulk action reads the backend before it has applied
        // the change, so filtering here - not in the query cache - is what
        // keeps them gone. WHEEL-359.
        return !suppressedThreatKeys.has(key);
      }),
    ],
    [pendingOperations, sortedItems, suppressedThreatKeys],
  );

  const getRowKey = useCallback(
    (item: any) =>
      `${item.threat_id}_${item.protected_asset_id}_${item.company_id}`,
    [],
  );

  const handleOpenDetailInNewTab = useCallback((item: any) => {
    const url = `/threats/${item.protected_asset_id}/${item.threat_id}/${item.company_id}`;
    window.open(url, '_blank');
  }, []);

  const handleOpenStatusDropdown = useCallback((rowKey: string) => {
    if (!tableContainerRef.current) return;
    const row = tableContainerRef.current.querySelector(
      `[data-rowkey="${rowKey}"]`,
    );
    if (!row) return;
    const selectEl = row.querySelector('[role="combobox"]') as HTMLElement;
    if (selectEl) {
      selectEl.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyboardSelect = useCallback(
    (item: any) => {
      if (!item) return;
      handleChangeSelectedThreats({
        domain: item.protected_asset_name,
        threat: item.threat_name,
        threatId: item.threat_id,
        companyId: item.company_id,
        domainId: item.protected_asset_id,
        status: item.identification_status?.value,
      });
    },
    [handleChangeSelectedThreats],
  );

  const effectiveModalOpen =
    isModalOpen || openRejectionModal || showShortcutsHelp;

  // Deliberately re-created each render: it must close over the current
  // threat list (Undo restore index). The nav hook reads shortcut handlers
  // through a ref, so identity churn is harmless.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applyVerdict = (threat: any, status: string) => {
    if (!threat || threat.isLoading) return;
    if (threat.identification_status?.value === status) return;
    handleChangeThreatStatus(threat, status);
  };

  const threatCustomShortcuts = useMemo<CustomShortcut[]>(
    () => [
      {
        keys: 's',
        handler: (ctx: ShortcutContext) => {
          if (ctx.focusedRowKey === null) return;
          handleOpenStatusDropdown(ctx.focusedRowKey);
        },
      },
      {
        keys: 'mod+s',
        handler: () => {
          if (!hasSelectedThreats) return;
          onBulkStatusChange();
        },
      },
      {
        keys: 'c',
        handler: (ctx: ShortcutContext) => {
          if (ctx.focusedRowKey === null || !ctx.focusedItem) return;
          if (getThreatUrl(ctx.focusedItem)) {
            copyThreatName(getThreatUrl(ctx.focusedItem));
          }
        },
      },
      {
        keys: 'o',
        handler: (ctx: ShortcutContext) => {
          if (ctx.focusedRowKey === null || !ctx.focusedItem) return;
          handleOpenDetailInNewTab(ctx.focusedItem);
        },
      },
      {
        keys: 'p',
        handler: () => toggleReviewPanel(),
      },
      // Review-mode verdicts — only active while the preview panel is open,
      // so single letters can't change a status without the analyst having
      // opted into the inbox flow. Undo stays available via the toast.
      {
        keys: 'y',
        handler: (ctx: ShortcutContext) => {
          if (!reviewPanelOpen) return;
          applyVerdict(ctx.focusedItem, 'confirmed');
        },
      },
      {
        keys: 'd',
        handler: (ctx: ShortcutContext) => {
          if (!reviewPanelOpen) return;
          applyVerdict(ctx.focusedItem, 'discarded');
        },
      },
      {
        keys: 'b',
        handler: (ctx: ShortcutContext) => {
          if (!reviewPanelOpen) return;
          applyVerdict(ctx.focusedItem, 'benign');
        },
      },
      {
        // 'm' — 't' belongs to takedown initiation in the threat cockpit.
        keys: 'm',
        handler: (ctx: ShortcutContext) => {
          if (!reviewPanelOpen) return;
          applyVerdict(ctx.focusedItem, 'trademark');
        },
      },
      {
        keys: 'w',
        handler: (ctx: ShortcutContext) => {
          if (!reviewPanelOpen) return;
          applyVerdict(ctx.focusedItem, 'owned');
        },
      },
    ],
    // applyVerdict is re-created each render (it closes over the current
    // threat list for Undo restore); the hook reads handlers through a ref,
    // so recreating the array keeps them fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleOpenStatusDropdown,
      hasSelectedThreats,
      onBulkStatusChange,
      copyThreatName,
      handleOpenDetailInNewTab,
      toggleReviewPanel,
      reviewPanelOpen,
      applyVerdict,
    ],
  );

  const threatShortcutsList = useMemo(
    () => [
      ...BASE_SHORTCUTS,
      ESCAPE_WITH_DESELECT,
      ...THREAT_TABLE_SHORTCUTS,
      ...REVIEW_MODE_SHORTCUTS,
      ...HELP_SHORTCUT,
      ...SELECTION_SHORTCUTS,
      ...PAGINATION_SHORTCUTS,
    ],
    [],
  );

  const { focusedRowKey, setFocusedRowKey, isRowFocused, tableContainerRef } =
    useTableKeyboardNavigation({
      items: displayItems,
      getRowKey,
      onExpand: handleExpandToggle,
      expandedRowKey,
      disabled: effectiveModalOpen,
      onSelect: handleKeyboardSelect,
      onSelectAll,
      onDeselectAll,
      currentPage,
      totalPages,
      onPageChange,
      onToggleHelp: () => setShowShortcutsHelp(prev => !prev),
      customShortcuts: threatCustomShortcuts,
    });

  const focusedThreat = useMemo(
    () =>
      displayItems.find((item: any) => getRowKey(item) === focusedRowKey) ??
      null,
    [displayItems, focusedRowKey, getRowKey],
  );

  // Inbox flow: while the preview panel is open, always keep a row focused
  // (first row on open and after page changes) so the panel is never empty.
  useEffect(() => {
    if (reviewPanelOpen && focusedRowKey === null && displayItems.length > 0) {
      setFocusedRowKey(getRowKey(displayItems[0]));
    }
  }, [
    reviewPanelOpen,
    focusedRowKey,
    displayItems,
    getRowKey,
    setFocusedRowKey,
  ]);

  const renderTableRow = (items: ThreatTypes, index: number) => {
    // Protect against undefined items
    if (!items?.threat_id) {
      return null;
    }

    // Visibility is decided in `displayItems` above. The old guard here tested
    // `hasError === false`, but operations are created without the flag at all,
    // so it never fired - it only looked like pending rows were being hidden.
    // WHEEL-359.

    const {
      threat_id: threatId,
      company_id,
      protected_asset_id,
      threat_name,
      protected_asset_name,
    } = items;

    const rowKey = `${threatId}_${protected_asset_id}_${company_id}`;
    const threatData = threatNetworkData?.[threatId];
    const domainInfo = threatData?.whois;
    const whoisoading = threatData?.whois_loading;
    const focused = isRowFocused(rowKey);

    const onSelectThreat = () => {
      handleChangeSelectedThreats({
        domain: protected_asset_name,
        threat: threat_name,
        threatId,
        companyId: company_id,
        domainId: protected_asset_id,
        status: items.identification_status?.value,
      });
    };

    return (
      <React.Fragment key={`${index}${items.threat_id}`}>
        <TableRow
          data-rowkey={rowKey}
          onClick={() => {
            handleExpandToggle(rowKey);
            setFocusedRowKey(rowKey);
          }}
          sx={{
            cursor: 'pointer',
            backgroundColor: pendingOperations?.[
              bulkOperationKeyForThreat(items)
            ]?.hasError
              ? 'rgba(255, 0, 0, 0.1)'
              : 'inherit',
            ...(focused ? focusedRowSx : {}),
          }}
        >
          <ExpandCell>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleExpandToggle(rowKey);
              }}
              data-testid="expand-threat-row"
            >
              {expandedRowKey === rowKey ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </ExpandCell>
          <ThreatUrlCell>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Checkbox
                  data-testid="checkbox-table"
                  checked={isThreatSelected({
                    threatId,
                    companyId: company_id,
                    domainId: protected_asset_id,
                  })}
                  onChange={() => onSelectThreat()}
                  onClick={e => e.stopPropagation()}
                  sx={{ paddingLeft: '0' }}
                />
                <Tooltip
                  title={getDisplayThreatName(items.value.value)}
                  placement="top"
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <ThreatDetails
                      data-testid="table-threat-name"
                      onClick={e => e.stopPropagation()}
                      to={`/threats/${protected_asset_id}/${threatId}/${company_id}`}
                      target="_blank"
                    >
                      {(() => {
                        const displayName = getDisplayThreatName(
                          items.value.value,
                        );
                        return displayName.length > MIN_THREAT_NAME_LENGTH
                          ? displayName.substring(0, MIN_THREAT_NAME_LENGTH) +
                              '...'
                          : displayName;
                      })()}
                    </ThreatDetails>
                    {isPunycode(items.value.value) && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {items.value.value}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
                {items.resurgent_threat && (
                  <Tooltip
                    title={`Resurgent threat — confirmed ${
                      items.identification_status?.updated_at
                        ? formatedDate(items.identification_status.updated_at)
                        : ''
                    }`}
                    placement="top"
                  >
                    <Box
                      sx={{ display: 'inline-flex' }}
                      data-testid="resurgent-threat-icon"
                    >
                      <ResurgentThreatIcon
                        size={18}
                        sx={{ color: 'warning.main' }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <ActionButtons className="action-buttons">
                <Tooltip title="Copy URL">
                  <IconButton
                    data-testid="table-copy-content"
                    onClick={e => {
                      e.stopPropagation();
                      copyThreatName(getThreatUrl(items));
                    }}
                    size="small"
                    sx={{ padding: 0 }}
                  >
                    <ContentCopyIcon sx={{ height: 20, width: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy Defanged URL">
                  <IconButton
                    data-testid="table-copy-defanged-content"
                    onClick={e => {
                      e.stopPropagation();
                      copyDefangedThreatName(getThreatUrl(items));
                    }}
                    size="small"
                    sx={{ padding: 0 }}
                  >
                    <ShieldIcon sx={{ height: 20, width: 20 }} />
                  </IconButton>
                </Tooltip>
              </ActionButtons>
            </Box>
          </ThreatUrlCell>
          <TableCell>{items?.protected_asset_name}</TableCell>
          <TableCell>
            <Typography
              variant="caption"
              component="a"
              href={`/companies/${encodeURIComponent(company_id)}/view`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s ease',
                '&:hover': {
                  color: 'primary.dark',
                  textDecoration: 'underline',
                },
              }}
            >
              {findCompanyNameById(company_id)}
            </Typography>
          </TableCell>
          <TableCell onClick={e => e.stopPropagation()}>
            {items.isLoading ? (
              <Skeleton
                animation="wave"
                variant="text"
                sx={{ fontSize: '1rem' }}
                width={'100%'}
              />
            ) : (
              <SelectOption
                size="small"
                width="120px"
                value={items.identification_status?.value}
                options={buildStatusOptions(items.identification_status?.value)}
                onChange={(e: any) => {
                  const newStatus = e.target.value;
                  handleChangeThreatStatus(items, newStatus);
                }}
              />
            )}
          </TableCell>
          <TableCell>
            {items.source ? (
              <SourceFaviconChip
                source={items.source}
                threatId={threatId}
                domainId={protected_asset_id}
              />
            ) : (
              <Span>-</Span>
            )}
          </TableCell>
          <TableCell>
            <BrpClassificationCell classification={items.brp_classification} />
          </TableCell>
          <TableCell>
            {whoisoading ? (
              <Skeleton animation="wave" variant="text" width="100%" />
            ) : (
              domainInfo?.registrar || <Span>No data available</Span>
            )}
          </TableCell>
          <TableCell>
            {whoisoading ? (
              <Skeleton animation="wave" variant="text" width="100%" />
            ) : domainInfo?.registration_date ? (
              `${formatedDate(domainInfo?.registration_date)} UTC`
            ) : (
              <Span>No data available</Span>
            )}
          </TableCell>
          <TableCell>
            {whoisoading ? (
              <Skeleton animation="wave" variant="text" width="100%" />
            ) : domainInfo?.expiration_date ? (
              `${formatedDate(domainInfo?.expiration_date)} UTC`
            ) : (
              <Span>No data available</Span>
            )}
          </TableCell>
          <TableCell>
            {items?.detection_date ? (
              `${formatedDate(items?.detection_date)} UTC`
            ) : (
              <Span>No data available</Span>
            )}
          </TableCell>
        </TableRow>
        <ThreatExpandedRow
          threat={items}
          isExpanded={expandedRowKey === rowKey}
          threatNetworkData={threatNetworkData?.[threatId]}
        />
      </React.Fragment>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Tooltip title="Toggle the threat preview panel (P)">
          <Button
            size="small"
            variant={reviewPanelOpen ? 'contained' : 'outlined'}
            startIcon={<ViewSidebarOutlinedIcon />}
            onClick={toggleReviewPanel}
            data-testid="threats-preview-toggle"
            sx={{ textTransform: 'none' }}
          >
            Preview
            <Kbd k="P" />
          </Button>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <TableContainer
          component={Paper}
          ref={tableContainerRef}
          tabIndex={0}
          sx={{
            flex: 1,
            minWidth: 0,
            boxShadow: (theme: any) =>
              theme.palette.mode === 'dark'
                ? 'none'
                : '0px 18px 32px 16px rgba(193, 193, 193, 0.16)',
            border: (theme: any) =>
              theme.palette.mode === 'dark'
                ? `1px solid ${theme.palette.divider}`
                : 'none',
            outline: 'none',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 28, padding: '0 2px' }} />
                {[
                  { label: 'Threat URL', key: 'threat_name' },
                  { label: 'Target Domain', key: 'protected_asset_name' },
                  { label: 'Company', key: 'company_name' },
                  { label: 'Status', key: 'identification_status.value' },
                  { label: 'Source', key: 'source' },
                  {
                    label: 'BRP Classification',
                    key: 'brp_classification.verdict',
                  },
                  { label: 'Registrar', key: 'domainInfo.registrar' },
                  {
                    label: 'Registration',
                    key: 'domainInfo.registration_date',
                  },
                  { label: 'Expiration', key: 'domainInfo.expiration_date' },
                  { label: 'Detection', key: 'detection_date' },
                ].map(column => {
                  const isActive = threatSort?.columnKey === column.key;
                  return (
                    <TableCell key={column.key}>
                      <TableSortLabel
                        data-testid={`table-sort-header-${column.key.replace(
                          /\./g,
                          '-',
                        )}`}
                        active={isActive}
                        direction={isActive ? threatSort?.sortDirection : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>{displayItems.map(renderTableRow)}</TableBody>
          </Table>
          <RejectionReasonsModal
            open={openRejectionModal}
            handleClose={() => setOpenRejectionModal(false)}
            handleConfirm={updateSingleStatusWithRejection}
            status={selectedThreat?.newStatus}
          />
          <KeyboardShortcutsHelp
            open={showShortcutsHelp}
            onClose={() => setShowShortcutsHelp(false)}
            shortcuts={threatShortcutsList}
          />
        </TableContainer>
        {reviewPanelOpen && (
          <ThreatPreviewPanel
            threat={focusedThreat}
            whois={threatNetworkData?.[focusedThreat?.threat_id]?.whois}
            whoisLoading={
              threatNetworkData?.[focusedThreat?.threat_id]?.whois_loading
            }
            companyName={
              focusedThreat ? findCompanyNameById(focusedThreat.company_id) : ''
            }
            onVerdict={applyVerdict}
            onClose={toggleReviewPanel}
          />
        )}
      </Box>
    </Box>
  );
};

export default ThreatTable;
